import React from "react";
import { FormControl, FormLabel } from "@chakra-ui/react";
import { CountryDropdown, RegionDropdown } from "react-country-region-selector";
import { Input, Textarea, Select } from "ui/inputs";
import { remCalc } from "theme/utils";
import { GdprOptIn } from "../gdpr-opt-in";

type ControlProps = {
  value: string;
  onChange: (value: string) => void;
  name: string;
  isRequired?: boolean;
  country?: string;
  label?: string;
  type?: string;
  fieldType?: string;
  options?: Array<{ label: string; value: string }>;
  isCustomField?: boolean;
  gdprColor?: string;
};

const customSelectStyles = {
  // Hover and focus styles don't render with this package, this is the best we can do
  bg: "white",
  border: "1px solid",
  borderColor: "var(--chakra-colors-brand-border-light)",
  borderRadius: "var(--chakra-radii-base)",
  fontSize: "var(--chakra-fontSizes-brand-sm)",
  lineHeight: "var(--chakra-lineHeights-20)",
  letterSpacing: remCalc(0.363),
  padding: "0 12px",
  height: "44px",
  width: "100%",
  "& option": {
    "&:first-of-type": {
      color: "var(--chakra-colors-brand-textSecondary-light)",
    },
  },
  // Style the select arrow icon
  appearance: "none",
  backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='m5 7.5 5 5 5-5' stroke='%236D7772' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 12px center",
  backgroundSize: "16px",
  paddingRight: "40px",
};

export function Control({
  name,
  isRequired = true,
  value,
  onChange,
  country,
  label,
  type,
  fieldType = "text",
  options,
  isCustomField,
}: ControlProps) {
  const handleChange = (event: any) => {
    onChange(event.target.value);
  };

  if (isCustomField) {
    return (
      <FormControl as="fieldset" id={name} isRequired={isRequired}>
        <FormLabel as="legend" fontSize="sm" sx={{ opacity: 0, height: 0, m: 0 }}>
          {label}
        </FormLabel>
        {fieldType === "text" && <Input type={type} name={name} placeholder={label} onChange={handleChange} />}
        {fieldType === "textarea" && <Textarea name={name} placeholder={label} onChange={handleChange} />}
        {fieldType === "select" && options && (
          <Select
            placeholder={label}
            name={name}
            color={value ? "brand.text.light" : "brand.textSecondary.light"}
            onChange={handleChange}
          >
            {options.map(({ label, value }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        )}
      </FormControl>
    );
  }

  switch (name) {
    case "email": {
      return (
        <FormControl as="fieldset" id="email" isRequired={isRequired}>
          <FormLabel as="legend" fontSize="sm" sx={{ opacity: 0, height: 0, m: 0 }}>
            Work email
          </FormLabel>
          <Input type="email" name={name} placeholder="Work email" onChange={handleChange} />
        </FormControl>
      );
    }

    case "company": {
      return (
        <FormControl as="fieldset" id="company">
          <FormLabel as="legend" fontSize="sm" sx={{ opacity: 0, height: 0, m: 0 }}>
            Company
          </FormLabel>
          <Input name={name} placeholder="Company" onChange={handleChange} />
        </FormControl>
      );
    }

    case "firstname": {
      return (
        <FormControl as="fieldset" id="firstname" isRequired={isRequired}>
          <FormLabel as="legend" fontSize="sm" sx={{ opacity: 0, height: 0, m: 0 }}>
            First name
          </FormLabel>
          <Input name={name} placeholder="First name" onChange={handleChange} />
        </FormControl>
      );
    }

    case "jobtitle": {
      return (
        <FormControl as="fieldset" id="jobtitle" isRequired={isRequired}>
          <FormLabel as="legend" fontSize="sm" sx={{ opacity: 0, height: 0, m: 0 }}>
            Job title
          </FormLabel>
          <Input name={name} placeholder="Job title" onChange={handleChange} />
        </FormControl>
      );
    }

    case "lastname": {
      return (
        <FormControl as="fieldset" id="lastname" isRequired={isRequired}>
          <FormLabel as="legend" fontSize="sm" sx={{ opacity: 0, height: 0, m: 0 }}>
            Last name
          </FormLabel>
          <Input name={name} placeholder="Last name" onChange={handleChange} />
        </FormControl>
      );
    }

    case "numberofemployees": {
      return (
        <FormControl as="fieldset" id="numberofemployees">
          <FormLabel as="legend" fontSize="sm" sx={{ opacity: 0, height: 0, m: 0 }}>
            Number of employees
          </FormLabel>
          <Input type="number" min={1} name={name} placeholder="Number of employees" onChange={handleChange} />
        </FormControl>
      );
    }

    case "country": {
      return (
        <FormControl as="fieldset" id="country">
          <FormLabel as="legend" fontSize="sm" sx={{ opacity: 0, height: 0, m: 0 }}>
            Country
          </FormLabel>
          <CountryDropdown
            showDefaultOption
            defaultOptionLabel="Select a country"
            name={name}
            value={value}
            // @ts-expect-error Country Dropdown accepts style props
            style={{
              ...customSelectStyles,
              color: value === "" ? "var(--chakra-colors-brand-textSecondary-light)" : "inherit",
            }}
            onChange={onChange}
          />
        </FormControl>
      );
    }

    case "state": {
      return country ? (
        <FormControl as="fieldset" id="region">
          <FormLabel as="legend" fontSize="sm" sx={{ opacity: 0, height: 0, m: 0 }}>
            Region
          </FormLabel>
          <RegionDropdown
            defaultOptionLabel="Select a region"
            country={country || ""}
            name={name}
            value={value}
            // @ts-expect-error Region Dropdown accepts style props
            style={{
              ...customSelectStyles,
              color: value === "" ? "var(--chakra-colors-brand-textSecondary-light)" : "inherit",
            }}
            onChange={onChange}
          />
        </FormControl>
      ) : null;
    }

    case "gdpr": {
      return <GdprOptIn name={name} onChange={handleChange} />;
    }

    default: {
      return null;
    }
  }
}
